import { Position, PositionRange } from '../../types'
import { Dispatch, SetStateAction, useContext, useMemo, useRef, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { StorageKey } from '../../enums/StorageKey'
import { CutterContext } from '../contexts/CutterContext'
import { useAudioPlayerGain } from './useAudioPlayerGain'
import { useGetPositionSeconds } from './usePositionSeconds'
import { isAudioChunkToPlay } from '../functions/isAudioChunkToPlay'
import { getAudioBufferChunkPlaybackTime } from '../functions/getAudioBufferChunkPlaybackTime'
import { Deferred } from '../../entities/Deferred'
import { AudioContextFactory } from '../contexts/AudioContextFactory'

export interface AudioPlayer {
  play: (range?: Position | PositionRange) => Promise<void>
  stop: () => void
  playback: boolean
  getPlaybackPosition: () => Position
  volume: number
  setVolume: Dispatch<SetStateAction<number>>
  mute: boolean
  setMute: Dispatch<SetStateAction<boolean>>
  rewind: (getPosition: (position: Position) => Position) => void
}

interface Playback {
  range: PositionRange
  imaginaryStartTime: number
  sources: Set<AudioScheduledSourceNode>
  deferred: Deferred
}

export function useAudioPlayer(): AudioPlayer {
  const [playback, setPlayback] = useState<Playback | null>(null)
  const [volume, setVolume] = useLocalStorage(StorageKey.Volume, 0.3)
  const [mute, setMute] = useLocalStorage(StorageKey.Mute, false)
  const { audioBuffer, audio } = useContext(CutterContext)
  const gain = useAudioPlayerGain(mute, volume)
  const sec = useGetPositionSeconds()
  const rewind = (getPosition: (position: Position) => Position) => {
    if (playback) {
      const next = getPosition(getPlaybackPosition())
      const [, end] = playback.range

      next > end ? stop() : play([next < 0 ? 0 : next, end])
    }
  }

  const deleteSource = (source: AudioScheduledSourceNode, playback: Playback) => {
    source.onended = null
    source.stop()
    source.disconnect()
    playback.sources.delete(source)
  }
  const stopPlayback = (playback: Playback) => {
    playback.sources.forEach((source) => deleteSource(source, playback))
    playback.deferred.resolve()
  }
  const stop = () => {
    if (playback) {
      stopPlayback(playback)
      setPlayback(null)
    }
  }

  function onSourceEnded(source: AudioScheduledSourceNode, playback: Playback) {
    deleteSource(source, playback)

    if (playback.sources.size === 0) {
      stopPlayback(playback)
      setPlayback(null)
    }
  }

  const play = (start: Position | PositionRange = 0) => {
    const range: PositionRange = typeof start === 'number' ? [start, audio.length] : start

    if (playback) {
      stopPlayback(playback)
    }

    const { currentTime } = AudioContextFactory.getAudioContext()
    const nextPlayback: Playback = {
      imaginaryStartTime: currentTime - range[0] / audioBuffer.sampleRate,
      range,
      deferred: new Deferred(),
      sources: new Set<AudioScheduledSourceNode>(),
    }

    audio.chunks.forEach((chunk) => {
      if (isAudioChunkToPlay(chunk, range)) {
        const { when, offset, length } = getAudioBufferChunkPlaybackTime(chunk, range)
        const source = AudioContextFactory.getAudioContext().createBufferSource()

        source.buffer = audioBuffer
        source.onended = () => onSourceEnded(source, nextPlayback)
        source.connect(gain)
        source.start(currentTime + sec(when), sec(offset), sec(length))

        nextPlayback.sources.add(source)
      }
    })

    setPlayback(nextPlayback)

    return nextPlayback.deferred.promise
  }
  const getPlaybackPosition = () => {
    return playback === null
      ? 0
      : Math.round(
          (AudioContextFactory.getAudioContext().currentTime - playback.imaginaryStartTime) * audioBuffer.sampleRate,
        )
  }

  const methodsRef = useRef({
    getPlaybackPosition,
    play,
    stop,
    rewind,
  })
  methodsRef.current = {
    getPlaybackPosition,
    play,
    stop,
    rewind,
  }

  return useMemo(
    () => ({
      play: (...args) => methodsRef.current.play(...args),
      stop: () => methodsRef.current.stop(),
      getPlaybackPosition: () => methodsRef.current.getPlaybackPosition(),
      playback: playback !== null,
      volume,
      setVolume,
      mute,
      setMute,
      rewind: methodsRef.current.rewind,
    }),
    [playback, volume, setVolume, mute, setMute],
  )
}
